import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import { salvarFoto } from "../../lib/foto";
import { dataHora, diasAtras } from "../../lib/format";
import { TendenciaLabel } from "../../ui/Tendencia";
import { calcularDerivadosExperiencia, statusSugeridoExperiencia } from "../domain";
import { fraseEngajamento } from "../evolucao";
import {
  arquivarExperiencia,
  definirStatusExperiencia,
  editarExperiencia,
  getExperiencia,
  getMomentosDaExperiencia,
} from "../queries";
import { tocarAudio } from "../audio";
import {
  ANDAMENTO_LABEL,
  CONTEXTO_LABEL,
  ENGAJAMENTO_LABEL,
  ESTADO_REGULACAO_LABEL,
  FAMILIARIDADE_LABEL,
  FUNCAO_LABEL,
  INTENSIDADE_LABEL,
  MODALIDADE_LABEL,
  ORIGEM_MOMENTO_LABEL,
  PREVISIBILIDADE_LABEL,
  STATUS_EXPERIENCIA_LABEL,
  TIPO_LABEL,
  type FuncaoMusical,
  type MomentoMusical,
  type StatusExperiencia,
} from "../types";
import { ExperienciaFoto } from "../ui/ExperienciaFoto";
import { StatusExperienciaBadge } from "../ui/StatusExperienciaBadge";
import { EngajamentoTimeline } from "../ui/EngajamentoTimeline";
import { EfeitoBadge } from "../ui/EfeitoBadge";
import { GravadorAudio } from "../ui/GravadorAudio";

const ORDEM_STATUS: StatusExperiencia[] = [
  "favorita",
  "em_exploracao",
  "nao_usada",
  "sensivel",
];

export function DetalheExperiencia() {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const experiencia = useLiveQuery(() => getExperiencia(id), [id]);
  const momentos = useLiveQuery(() => getMomentosDaExperiencia(id), [id]) ?? [];
  const [mostrarGravador, setMostrarGravador] = useState(false);
  const [salvandoAudio, setSalvandoAudio] = useState(false);

  if (!experiencia) {
    return (
      <main className="tela">
        <button className="btn btn-fantasma" onClick={() => nav(-1)}>
          ← voltar
        </button>
        <div className="vazio">experiência não encontrada.</div>
      </main>
    );
  }

  const d = calcularDerivadosExperiencia(id, momentos);
  const sugerido = statusSugeridoExperiencia(experiencia.status, d);
  const sonoro = experiencia.sonoro;
  const chipsSonoros = [
    sonoro.andamento && ANDAMENTO_LABEL[sonoro.andamento],
    sonoro.intensidade && INTENSIDADE_LABEL[sonoro.intensidade],
    sonoro.timbre,
    sonoro.previsibilidade && `previsib.: ${PREVISIBILIDADE_LABEL[sonoro.previsibilidade]}`,
    sonoro.familiaridade && FAMILIARIDADE_LABEL[sonoro.familiaridade],
  ].filter(Boolean) as string[];

  async function anexarBlob(blob: Blob) {
    if (!experiencia) return;
    setSalvandoAudio(true);
    try {
      const ref = await salvarFoto(blob);
      await editarExperiencia(id, { midias: [...experiencia.midias, ref] });
      setMostrarGravador(false);
    } finally {
      setSalvandoAudio(false);
    }
  }

  async function anexarArquivo(file: File | undefined) {
    if (!file) return;
    await anexarBlob(file);
  }

  return (
    <main className="tela">
      <button className="btn btn-fantasma" onClick={() => nav(-1)}>
        ← voltar
      </button>

      <div className="row" style={{ marginBottom: "var(--s4)" }}>
        <label className="foto-editavel" title="adicionar foto">
          <ExperienciaFoto midias={experiencia.midias} nome={experiencia.nome} tipo={experiencia.tipo} />
          <span className="foto-editavel-cam" aria-hidden>📷</span>
          <input
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={(e) => {
              void anexarArquivo(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
        </label>
        <div className="crescer">
          <h1 className="tela-titulo" style={{ marginBottom: 4 }}>
            {experiencia.nome}
          </h1>
          <div className="meta" style={{ color: "var(--text-muted)" }}>
            {TIPO_LABEL[experiencia.tipo]}
          </div>
        </div>
        <StatusExperienciaBadge status={experiencia.status} />
      </div>

      <div className="row" style={{ gap: "var(--s2)", marginBottom: "var(--s2)" }}>
        <button
          className="btn btn-fantasma crescer"
          aria-pressed={mostrarGravador}
          onClick={() => setMostrarGravador((v) => !v)}
        >
          🎙️ {mostrarGravador ? "fechar" : "gravar"}
        </button>
        <label className="btn btn-fantasma crescer" style={{ cursor: "pointer" }}>
          📎 anexar
          <input
            type="file"
            accept="audio/*"
            hidden
            onChange={(e) => {
              void anexarArquivo(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
        </label>
        <button className="btn btn-fantasma crescer" onClick={() => void tocarAudio(experiencia)}>
          ▶ ouvir
        </button>
      </div>

      {mostrarGravador && (
        <div style={{ marginBottom: "var(--s4)" }}>
          <GravadorAudio onSalvar={anexarBlob} salvando={salvandoAudio} />
        </div>
      )}

      <button
        className="btn btn-primario btn-bloco btn-grande"
        onClick={() => nav(`/compasso/registrar/${id}`)}
        style={{ marginBottom: "var(--s5)" }}
      >
        🎵 registrar momento
      </button>

      {/* P0.4 — contador + evolução do engajamento + efeito regulatório */}
      <section className="card">
        <div className="row-entre">
          <div>
            <div style={{ fontSize: "1.6rem", fontWeight: 500 }}>{d.n_momentos}</div>
            <div className="meta" style={{ color: "var(--text-muted)" }}>
              {d.n_momentos === 1 ? "momento" : "momentos"} · {diasAtras(d.dias_desde_ultimo)}
            </div>
          </div>
          <TendenciaLabel tendencia={d.tendencia} />
        </div>

        <div style={{ marginTop: "var(--s4)" }}>
          <EngajamentoTimeline momentos={momentos} />
        </div>

        <p className="evol-frase">{fraseEngajamento(experiencia.nome, momentos, d)}</p>

        <div className="efeito-linha">
          efeito regulatório: <EfeitoBadge efeito={d.efeito_regulatorio} forte />
        </div>

        {d.passo_maximo !== null && (
          <div className="meta" style={{ color: "var(--text-muted)", marginTop: 4 }}>
            maior passo: {ENGAJAMENTO_LABEL[d.passo_maximo]}
          </div>
        )}
      </section>

      {/* status — o cuidador decide; o app só sugere */}
      <section className="secao" style={{ marginTop: "var(--s4)" }}>
        <h2 className="secao-titulo">situação</h2>
        <div className="card">
          <div className="status-opcoes">
            {ORDEM_STATUS.map((s) => (
              <button
                key={s}
                type="button"
                className={`status-opt${experiencia.status === s ? " sel" : ""}`}
                aria-pressed={experiencia.status === s}
                onClick={() => definirStatusExperiencia(id, s)}
              >
                {STATUS_EXPERIENCIA_LABEL[s]}
              </button>
            ))}
          </div>
          <p className="status-regra">
            <strong>favorita</strong> = funciona bem · <strong>em exploração</strong> =
            experimentando · <strong>não usada</strong> = ainda não registrou ·
            <strong> sensível</strong> = a criança se desregula/rejeita (um dado a
            respeitar, não um fracasso — fica fora do modo escolha).
          </p>
          {sugerido !== experiencia.status && (
            <button
              className="btn btn-fantasma"
              style={{ marginTop: "var(--s2)" }}
              onClick={() => definirStatusExperiencia(id, sugerido)}
            >
              sugestão pelos dados: {STATUS_EXPERIENCIA_LABEL[sugerido]} — aplicar
            </button>
          )}

          <div className="campo" style={{ margin: "var(--s4) 0 0" }}>
            <label>função pretendida</label>
            <select
              value={experiencia.funcao ?? ""}
              onChange={(e) =>
                editarExperiencia(id, { funcao: (e.target.value || null) as FuncaoMusical | null })
              }
            >
              <option value="">—</option>
              {(Object.keys(FUNCAO_LABEL) as FuncaoMusical[]).map((f) => (
                <option key={f} value={f}>
                  {FUNCAO_LABEL[f]}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {experiencia.modalidade.length > 0 && (
        <section className="secao">
          <h2 className="secao-titulo">participação</h2>
          <div className="chips">
            {experiencia.modalidade.map((m) => (
              <span className="chip" key={m}>
                {MODALIDADE_LABEL[m]}
              </span>
            ))}
          </div>
        </section>
      )}

      {chipsSonoros.length > 0 && (
        <section className="secao">
          <h2 className="secao-titulo">perfil sonoro</h2>
          <div className="chips">
            {chipsSonoros.map((s) => (
              <span className="chip" key={s}>
                {s}
              </span>
            ))}
          </div>
        </section>
      )}

      {experiencia.observacoes && (
        <section className="secao">
          <h2 className="secao-titulo">observações</h2>
          <div className="card">{experiencia.observacoes}</div>
        </section>
      )}

      {/* histórico */}
      <section className="secao">
        <h2 className="secao-titulo">histórico</h2>
        {momentos.length === 0 ? (
          <div className="card" style={{ color: "var(--text-muted)" }}>
            sem momentos ainda.
          </div>
        ) : (
          momentos.map((m) => (
            <div className="card" key={m.id}>
              <div className="row-entre">
                <span style={{ fontWeight: 500 }}>
                  {ENGAJAMENTO_LABEL[m.passo_engajamento]}
                </span>
                <span className="meta" style={{ color: "var(--text-muted)" }}>
                  {dataHora(m.data_hora)}
                </span>
              </div>
              <div className="meta" style={{ color: "var(--text-suave)", marginTop: 4 }}>
                {origemDescricao(m)}
                {regulacaoDescricao(m)}
                {!m.confirmado && " · pendente"}
              </div>
            </div>
          ))
        )}
      </section>

      <button
        className="btn btn-bloco"
        style={{ marginTop: "var(--s4)", color: "var(--text-muted)" }}
        onClick={async () => {
          await arquivarExperiencia(id);
          nav("/compasso/biblioteca", { replace: true });
        }}
      >
        arquivar experiência
      </button>
    </main>
  );
}

function origemDescricao(m: MomentoMusical): string {
  const base = m.origem === "crianca" ? "escolha da criança" : ORIGEM_MOMENTO_LABEL.cuidador;
  return m.contexto ? `${base} · ${CONTEXTO_LABEL[m.contexto]}` : base;
}

function regulacaoDescricao(m: MomentoMusical): string {
  if (!m.estado_antes && !m.estado_depois) return "";
  const a = m.estado_antes ? ESTADO_REGULACAO_LABEL[m.estado_antes] : "?";
  const dpo = m.estado_depois ? ESTADO_REGULACAO_LABEL[m.estado_depois] : "?";
  return ` · ${a} → ${dpo}`;
}
